/**
 * Product Browser filter-pill model.
 *
 * Mirrors the pattern already proven on Staged Products
 * (`utils/stagedFilterState.ts` + `components/stagedProducts/*`): pills are
 * data, not a fixed row of controls, so the bar only shows what is actually
 * applied. Brand and Status are pinned (always shown, never removed) —
 * Category and Store sync are added on demand.
 *
 * This module intentionally stays small: the page already owns real state
 * for every filter (brandFilter/storeBrandFilters/categoryFilter/
 * catalogStatus/syncStatusFilter) and its own URL sync, so this file only
 * supplies the shared *shapes* the pill/menu components need plus small
 * label-formatting helpers — not a parallel state container.
 */

import { catalogTextMatchesSearch } from './catalogSearchMatch';

export type ProductPillKey = 'brand' | 'category' | 'status' | 'sync' | 'added' | 'match' | 'lifecycle';

export interface ProductFilterMark {
  logoUrl?: string;
  initial: string;
  color: string;
}

export interface ProductFilterOption {
  value: string;
  label: string;
  /** Facet count, when the API/loaded data actually provides one — never fabricated. */
  hint?: string;
  mark?: ProductFilterMark;
}

export type ProductFilterValue = string | string[];

export interface ProductFilterFieldDef {
  key: ProductPillKey;
  label: string;
  /** Multi-select fields carry string[]; single-select fields hold one value. */
  multi: boolean;
  /** Brand-style split: clicking the name selects only that value, the checkbox adds to the selection. */
  exclusiveDefault?: boolean;
  /** Pill label when a multi field has nothing selected. */
  allLabel?: string;
  searchable?: boolean;
  searchPlaceholder?: string;
  /** Always in the bar, never removable (Brand, Status). */
  pinned?: boolean;
  /** Vendor role: brand is fixed to their own brand, pill is inert. */
  locked?: boolean;
  /** One-line explanation shown at the top of the value menu. */
  hint?: string;
  /** Right-hand annotation in the "+ Add filter" menu (e.g. "follows brand", "store users"). */
  menuHint?: string;
  menuHintColor?: 'primary' | 'muted';
}

/** Pill display value: "All brands" / "Nikon" / "Nikon +2" / "Active" / "Any". */
export function productFilterValueLabel(
  field: ProductFilterFieldDef,
  value: ProductFilterValue,
  optionLabel?: (value: string) => string,
): string {
  const labelFor = (v: string) => (optionLabel ? optionLabel(v) : v);
  if (!field.multi) return value ? labelFor(String(value)) : 'Any';
  const values = Array.isArray(value) ? value : [];
  if (values.length === 0) return field.allLabel || 'All';
  if (values.length === 1) return labelFor(values[0]);
  return `${labelFor(values[0])} +${values.length - 1}`;
}

/** Whether a field is at a value worth calling out as "applied" (drives dim vs. blue, and Clear filters). */
export function productFilterIsActive(field: ProductFilterFieldDef, value: ProductFilterValue, defaultValue?: string): boolean {
  if (field.multi) return Array.isArray(value) && value.length > 0;
  return defaultValue != null ? value !== defaultValue : !!value;
}

/**
 * "Added" filter value (flag `product_browser_added_filter`): `''` is off,
 * `7d` / `30d` / `90d` are rolling presets, `since:YYYY-MM-DD` is a custom
 * date. The preset stays relative in a saved view and is turned into a date
 * each time the request is made.
 */
export const ADDED_FILTER_PRESETS = [
  { value: '7d', label: 'Last 7 days', days: 7 },
  { value: '30d', label: 'Last 30 days', days: 30 },
  { value: '90d', label: 'Last 90 days', days: 90 },
] as const;

const ADDED_SINCE_PREFIX = 'since:';
const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/;

export function isAddedFilterValue(value: unknown): value is string {
  if (typeof value !== 'string') return false;
  if (value === '') return true;
  if (ADDED_FILTER_PRESETS.some((p) => p.value === value)) return true;
  return value.startsWith(ADDED_SINCE_PREFIX) && ISO_DAY.test(value.slice(ADDED_SINCE_PREFIX.length));
}

export function customAddedFilterValue(isoDay: string): string {
  return ISO_DAY.test(isoDay) ? `${ADDED_SINCE_PREFIX}${isoDay}` : '';
}

/** The ISO instant the `addedSince` query parameter carries, or undefined when off. */
export function addedFilterToSince(value: string, now: Date = new Date()): string | undefined {
  const preset = ADDED_FILTER_PRESETS.find((p) => p.value === value);
  if (preset) {
    const start = new Date(now.getFullYear(), now.getMonth(), now.getDate() - preset.days);
    return start.toISOString();
  }
  if (value.startsWith(ADDED_SINCE_PREFIX) && ISO_DAY.test(value.slice(ADDED_SINCE_PREFIX.length))) {
    const [y, m, d] = value.slice(ADDED_SINCE_PREFIX.length).split('-').map(Number);
    return new Date(y, m - 1, d).toISOString();
  }
  return undefined;
}

/** The user's local calendar day as YYYY-MM-DD (not the UTC day `toISOString` gives). */
export function localIsoDay(date: Date = new Date()): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

export function addedFilterLabel(value: string): string {
  const preset = ADDED_FILTER_PRESETS.find((p) => p.value === value);
  if (preset) return preset.label;
  if (value.startsWith(ADDED_SINCE_PREFIX)) return `Since ${value.slice(ADDED_SINCE_PREFIX.length)}`;
  return 'Any time';
}

/**
 * "Matched to my store" filter value (flag `product_browser_match_filter`).
 * Matched means the product has a row in the store's SKU crosswalk (or a
 * legacy SKU override); "in sync" is membership in the sync selection the
 * browser already highlights.
 */
export const MATCH_FILTER_VALUES = ['all', 'matched_in_sync', 'matched_not_in_sync', 'not_matched'] as const;
export type MatchFilterValue = (typeof MATCH_FILTER_VALUES)[number];

export function isMatchFilterValue(value: unknown): value is MatchFilterValue {
  return typeof value === 'string' && (MATCH_FILTER_VALUES as readonly string[]).includes(value);
}

/**
 * Lifecycle filter value (flag `preorder_lifecycle`): the pre-release stage the
 * API serves on each row as `lifecycle.stage`. A row with no lifecycle is
 * released, so `released` keeps every ordinary product too. `prerelease` is
 * "not released yet": announced or pre-order.
 */
export const LIFECYCLE_FILTER_VALUES = ['all', 'prerelease', 'announced', 'preorder', 'released'] as const;
export type LifecycleFilterValue = (typeof LIFECYCLE_FILTER_VALUES)[number];

export function isLifecycleFilterValue(value: unknown): value is LifecycleFilterValue {
  return typeof value === 'string' && (LIFECYCLE_FILTER_VALUES as readonly string[]).includes(value);
}

/** The stage a product row is in; no lifecycle means released. */
export function lifecycleStageOf(row: { lifecycle?: { stage?: string } | null }): 'announced' | 'preorder' | 'released' {
  const stage = row.lifecycle?.stage;
  return stage === 'announced' || stage === 'preorder' ? stage : 'released';
}

/** Whether a stage pick admits a row in `stage`. */
export function lifecycleFilterAdmits(value: LifecycleFilterValue, stage: 'announced' | 'preorder' | 'released'): boolean {
  if (value === 'all') return true;
  if (value === 'prerelease') return stage !== 'released';
  return stage === value;
}

/** Keep only the rows in the picked stage; `all` returns the input untouched. */
export function applyLifecycleFilter<T extends { lifecycle?: { stage?: string } | null }>(rows: T[], value: LifecycleFilterValue): T[] {
  if (value === 'all') return rows;
  return rows.filter((row) => lifecycleFilterAdmits(value, lifecycleStageOf(row)));
}

/**
 * Admin product browser: its catalog reads carry no lifecycle, so the page joins
 * the pre-release set (`GET /admin/products/pre-release`) onto the rows it shows.
 * A row the set does not name keeps what it had (released).
 */
export function attachLifecycle<T extends { id: string; lifecycle?: unknown }>(
  rows: T[],
  lifecycleById: ReadonlyMap<string, T['lifecycle']>,
): T[] {
  if (lifecycleById.size === 0) return rows;
  return rows.map((row) => {
    const lifecycle = lifecycleById.get(row.id);
    return lifecycle ? { ...row, lifecycle } : row;
  });
}

export interface PreReleaseRowCriteria {
  search?: string;
  brand?: string;
  category?: string;
  productType?: string;
  /** `active` (default), `archived` (archived or discontinued) or `all`, as the catalog read means them. */
  catalogStatus?: 'active' | 'archived' | 'all';
  /** Name order, as the catalog read sorts (`localeCompare` on the name); default A to Z. */
  sort?: 'name_asc' | 'name_desc';
}

const foldText = (value: unknown): string => (typeof value === 'string' ? value.toLowerCase() : '');
const foldBrand = (value: unknown): string => foldText(value).replace(/[\s_-]+/g, '');

/**
 * The admin browser's "not released yet" views list the pre-release set itself
 * (a few products, read whole) rather than a server page, so the page's other
 * filters and its sort are applied here, the way the catalog read applies them:
 * search through the catalog matcher (`catalogSearchMatch`, the API's own copy),
 * brand by name (spacing and hyphens ignored), category, type, status, then name order.
 */
export function filterPreReleaseRows<
  T extends {
    name?: string; model?: string; sku?: string; mpn?: string; upc?: string; ean?: string; brand?: string;
    shortDescription?: string; description?: string; category?: string; productType?: string; status?: string;
  },
>(rows: T[], criteria: PreReleaseRowCriteria): T[] {
  const search = (criteria.search || '').trim();
  const brand = foldBrand(criteria.brand);
  const category = foldText(criteria.category);
  const status = criteria.catalogStatus ?? 'active';
  const kept = rows.filter((row) => {
    if (status === 'active' && (row.status ?? 'active') !== 'active') return false;
    if (status === 'archived' && row.status !== 'archived' && row.status !== 'discontinued') return false;
    if (brand && foldBrand(row.brand) !== brand) return false;
    if (category && foldText(row.category) !== category) return false;
    if (criteria.productType && row.productType !== criteria.productType) return false;
    if (
      search &&
      !catalogTextMatchesSearch(
        [row.name, row.model, row.brand, row.sku, row.mpn, row.upc, row.ean, row.description, row.shortDescription],
        search,
      )
    ) {
      return false;
    }
    return true;
  });
  const direction = criteria.sort === 'name_desc' ? -1 : 1;
  return kept.sort((a, b) => direction * (a.name || '').localeCompare(b.name || ''));
}

/** Keep only the rows the match filter admits; `all` returns the input untouched. */
export function applyMatchFilter<T extends { id: string }>(
  rows: T[],
  value: MatchFilterValue,
  matchedIds: ReadonlySet<string>,
  syncSelectedIds: ReadonlySet<string>,
): T[] {
  switch (value) {
    case 'matched_in_sync':
      return rows.filter((r) => matchedIds.has(r.id) && syncSelectedIds.has(r.id));
    case 'matched_not_in_sync':
      return rows.filter((r) => matchedIds.has(r.id) && !syncSelectedIds.has(r.id));
    case 'not_matched':
      return rows.filter((r) => !matchedIds.has(r.id));
    default:
      return rows;
  }
}
